import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { 
  format, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth,
  startOfYear,
  endOfYear,
  eachDayOfInterval,
  isSameDay,
  isWithinInterval,
  addDays,
  parseISO
} from 'date-fns';
import es from 'date-fns/locale/es';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  CalendarIcon,
  ClockIcon,
  UserGroupIcon,
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import EventService from '../../services/EventService';
import CreateEventModal from './CreateEventModal';
import toast from 'react-hot-toast';

const CalendarView = () => {
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'year'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createEventDate, setCreateEventDate] = useState(null);

  // Cargar eventos al montar el componente
  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const eventsData = await EventService.getEvents();
      const formattedEvents = EventService.formatEventsForCalendar(eventsData);
      setEvents(formattedEvents);
    } catch (error) {
      toast.error('Error al cargar los eventos');
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar el clic en crear evento
  const handleCreateEvent = (date) => {
    setCreateEventDate(date);
    setIsCreateModalOpen(true);
  };

  // Función para cuando se crea un evento exitosamente
  const handleEventCreated = () => {
    loadEvents(); // Recargar eventos
  };

  // Obtener eventos para una fecha específica
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      return isSameDay(date, eventStart) || 
             isSameDay(date, eventEnd) ||
             (date >= eventStart && date <= eventEnd);
    });
  };

  // Tile content para el calendario (mostrar puntos en días con eventos)
  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dayEvents = getEventsForDate(date);
      if (dayEvents.length > 0) {
        return (
          <div className="flex justify-center gap-1 mt-1">
            {dayEvents.slice(0, 3).map((event, idx) => (
              <div 
                key={idx} 
                className="w-1.5 h-1.5 rounded-full bg-primary-600"
              />
            ))}
          </div>
        );
      } else {
        // Mostrar un indicador sutil para días sin eventos
        return (
          <div className="flex justify-center mt-1">
            <div className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600 opacity-50" />
          </div>
        );
      }
    }
    return null;
  };

  // Navegar entre fechas
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    
    if (viewMode === 'month') {
      newDate.setMonth(currentDate.getMonth() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + (direction * 7));
    } else if (viewMode === 'year') {
      newDate.setFullYear(currentDate.getFullYear() + direction);
    }
    
    setCurrentDate(newDate);
  };

  // Renderizar vista de mes (usando react-calendar)
  const renderMonthView = () => {
    return (
      <div className="calendar-container">
        <Calendar
          key={`calendar-${currentDate.getTime()}`}
          value={currentDate}
          onChange={setCurrentDate}
          locale="es-ES"
          tileContent={tileContent}
          className="w-full rounded-lg border-none shadow-lg"
          onClickDay={(date) => {
            // Si el día tiene eventos, mostrar modal de eventos del día
            // Si no tiene eventos, abrir modal de crear evento
            const dayEvents = getEventsForDate(date);
            if (dayEvents.length > 0) {
              setSelectedDate(date);
            } else {
              handleCreateEvent(date);
            }
          }}
        />
        
        {/* Botón flotante para crear evento */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={() => handleCreateEvent(currentDate)}
            className="btn-primary flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Crear Evento</span>
          </button>
        </div>
      </div>
    );
  };

  // Renderizar vista de semana
  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    return (
      <div className="grid grid-cols-7 gap-2">
        {days.map((day) => {
          const dayEvents = getEventsForDate(day);
          const isToday = isSameDay(day, new Date());
          
          return (
            <div 
              key={day.toString()} 
              className={`card p-3 min-h-[150px] cursor-pointer hover:shadow-lg transition-shadow relative group ${
                isToday ? 'border-2 border-primary-600' : ''
              }`}
              onClick={() => {
                // Si el día tiene eventos, mostrar modal de eventos del día
                // Si no tiene eventos, abrir modal de crear evento
                if (dayEvents.length > 0) {
                  setSelectedDate(day);
                } else {
                  handleCreateEvent(day);
                }
              }}
            >
              <div className="text-center mb-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">
                  {format(day, 'EEE', { locale: es })}
                </div>
                <div className={`text-lg font-semibold ${
                  isToday ? 'text-primary-600' : 'text-gray-900 dark:text-white'
                }`}>
                  {format(day, 'd')}
                </div>
              </div>
              <div className="space-y-1">
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    className="text-xs p-2 bg-primary-100 dark:bg-primary-900 rounded cursor-pointer hover:bg-primary-200 dark:hover:bg-primary-800 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEvent(event);
                    }}
                  >
                    <div className="font-semibold text-primary-900 dark:text-primary-100 truncate">
                      {event.title}
                    </div>
                    <div className="text-primary-700 dark:text-primary-300">
                      {format(event.start, 'HH:mm')}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Botón para crear evento en este día */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCreateEvent(day);
                }}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-primary-600 text-white rounded-full hover:bg-primary-700"
                title="Crear evento"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // Renderizar vista de año
  const renderYearView = () => {
    const months = Array.from({ length: 12 }, (_, i) => {
      const date = new Date(currentDate.getFullYear(), i, 1);
      return date;
    });

    return (
      <div className="grid grid-cols-3 gap-4">
        {months.map((month) => {
          const monthStart = startOfMonth(month);
          const monthEnd = endOfMonth(month);
          const monthEvents = events.filter(event => 
            isWithinInterval(event.start, { start: monthStart, end: monthEnd }) ||
            isWithinInterval(event.end, { start: monthStart, end: monthEnd })
          );

          return (
            <div 
              key={month.toString()} 
              className="card p-4 cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => {
                setCurrentDate(month);
                setViewMode('month');
              }}
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {format(month, 'MMMM', { locale: es })}
              </h3>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {monthEvents.length} {monthEvents.length === 1 ? 'evento' : 'eventos'}
              </div>
              {monthEvents.length > 0 && (
                <div className="mt-2 space-y-1">
                  {monthEvents.slice(0, 3).map((event) => (
                    <div 
                      key={event.id}
                      className="text-xs p-1 bg-primary-50 dark:bg-primary-900 rounded truncate"
                    >
                      {event.title}
                    </div>
                  ))}
                  {monthEvents.length > 3 && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      +{monthEvents.length - 3} más
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // Modal de detalles del evento
  const EventDetailsModal = ({ event, onClose }) => {
    if (!event) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="card max-w-2xl w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {event.title}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {event.description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                    Descripción
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {event.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    Inicio
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {format(event.start, "dd 'de' MMMM, yyyy 'a las' HH:mm", { locale: es })}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-1" />
                    Fin
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {format(event.end, "dd 'de' MMMM, yyyy 'a las' HH:mm", { locale: es })}
                  </p>
                </div>
              </div>

              {event.participants && event.participants.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                    <UserGroupIcon className="h-4 w-4 mr-1" />
                    Participantes
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {event.participants.map((participant, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm text-gray-700 dark:text-gray-300"
                      >
                        {participant}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                  event.status === 'active' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                  {event.status === 'active' ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // Modal de eventos del día
  const DayEventsModal = ({ date, onClose }) => {
    if (!date) return null;
    
    const dayEvents = getEventsForDate(date);

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="card max-w-2xl w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {format(date, "dd 'de' MMMM, yyyy", { locale: es })}
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    onClose();
                    // Abrir modal de crear evento con la fecha seleccionada
                    setTimeout(() => {
                      handleCreateEvent(date);
                    }, 100);
                  }}
                  className="btn-primary flex items-center space-x-1 px-3 py-1 text-sm"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Nuevo Evento</span>
                </button>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {dayEvents.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No hay eventos para este día
                </p>
                <button
                  onClick={() => {
                    onClose();
                    // Abrir modal de crear evento con la fecha seleccionada
                    setTimeout(() => {
                      handleCreateEvent(date);
                    }, 100);
                  }}
                  className="btn-primary flex items-center space-x-2 mx-auto"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Crear Evento</span>
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {dayEvents.map((event) => (
                  <div
                    key={event.id}
                    className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    onClick={() => {
                      setSelectedDate(null);
                      setSelectedEvent(event);
                    }}
                  >
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
                      {event.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {event.description}
                    </p>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };

  // Obtener el título del encabezado según el modo de vista
  const getHeaderTitle = () => {
    if (viewMode === 'year') {
      return currentDate.getFullYear();
    } else if (viewMode === 'month') {
      return format(currentDate, 'MMMM yyyy', { locale: es });
    } else {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
      return `${format(weekStart, 'd MMM', { locale: es })} - ${format(weekEnd, 'd MMM yyyy', { locale: es })}`;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto"
      >
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Calendario de Eventos
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gestiona y visualiza todos tus eventos
          </p>
        </div>

        {/* Controles */}
        <div className="card p-4 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Navegación de fecha */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigateDate(-1)}
                className="btn-secondary p-2"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white min-w-[200px] text-center capitalize">
                {getHeaderTitle()}
              </h2>
              
              <button
                onClick={() => navigateDate(1)}
                className="btn-secondary p-2"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>

              <button
                onClick={() => {
                  const today = new Date();
                  setCurrentDate(today);
                  // Si estamos en vista de año, cambiar a vista de mes para mostrar el mes actual
                  if (viewMode === 'year') {
                    setViewMode('month');
                  }
                }}
                className="btn-secondary"
              >
                Hoy
              </button>
            </div>

            {/* Selector de vista */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('month')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'month'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Mes
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'week'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Semana
              </button>
              <button
                onClick={() => setViewMode('year')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  viewMode === 'year'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                Año
              </button>
            </div>
          </div>
        </div>

        {/* Contenido del calendario */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        ) : (
          <div className="card p-6">
            {viewMode === 'month' && renderMonthView()}
            {viewMode === 'week' && renderWeekView()}
            {viewMode === 'year' && renderYearView()}
          </div>
        )}

        {/* Leyenda de eventos */}
        <div className="mt-6 card p-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            Próximos Eventos
          </h3>
          <div className="space-y-2">
            {events
              .filter(event => event.start >= new Date())
              .sort((a, b) => a.start - b.start)
              .slice(0, 5)
              .map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => setSelectedEvent(event)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary-600"></div>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {event.title}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {format(event.start, "dd MMM, HH:mm", { locale: es })}
                      </div>
                    </div>
                  </div>
                  <CalendarIcon className="h-5 w-5 text-gray-400" />
                </div>
              ))}
            {events.filter(event => event.start >= new Date()).length === 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                No hay eventos próximos
              </p>
            )}
          </div>
        </div>

        {/* Modales */}
        {selectedEvent && (
          <EventDetailsModal 
            event={selectedEvent} 
            onClose={() => setSelectedEvent(null)} 
          />
        )}
        
        {selectedDate && (
          <DayEventsModal 
            date={selectedDate} 
            onClose={() => setSelectedDate(null)} 
          />
        )}

        {/* Modal de crear evento */}
        <CreateEventModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          selectedDate={createEventDate}
          onEventCreated={handleEventCreated}
        />
      </motion.div>
    </div>
  );
};

export default CalendarView;
